// Receive every line of one order in one press: /receiving/order/:poId, and the
// same page for a single headerless line at /receiving/line/:lineId.
//
// THE COUNT NEVER PREFILLS. Every count field opens empty and only a person
// fills it; the "All N" chip is an act with the number printed on it, and a
// blank line means "not in this delivery". ReceiveDrawer.tsx carries the
// history of why (the 108 that were never on the bench).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { Tag } from '../components/ui/Tag';
import FadeIn from '../components/ui/FadeIn';
import Reveal from '../components/ui/Reveal';
import { Checkbox } from '../components/ui/Checkbox';
import { Field } from '../components/ui/Field';
import { Select } from '../components/ui/Select';
import BackLink from '../components/BackLink';
import { Link, Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import PageHeader, { SecondaryAction } from '../components/PageHeader';
import ItemName, { itemLabel } from '../components/ItemName';
import EntityCombobox, { type ComboboxOption } from '../components/EntityCombobox';
import { modalLabel } from '../components/review/fieldStyles';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { formatEventDate, formatTimestamp } from '../utils/date-utils';
import ReceiverIdentityModal from './ReceiverIdentityModal';
import DrawerShell from './DrawerShell';
import { openAuthedPdf } from './openAuthedPdf';
import PageLayout from '../components/ui/PageLayout';
import { Alert } from '../components/ui/Alert';
import { EmptyState } from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { receiverErrorMessage } from './receivedAs';
import type { PendingSlipLine } from './packingListApi';
import {
  errorBody,
  errorCode,
  overReceiptRefusal,
  useReceiveDeliveryApi,
  type OverReceiptRefusal,
} from './receiveDeliveryApi';
import type {
  LocationRow,
  OrderReceiveLine,
  ReceiveDeliveryLineInput,
  ReceiveDeliveryRequest,
  ShippingCarrier,
  OverReceiptLine,
} from './types';

// ── Small pieces ─────────────────────────────────────────────────────────────

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';
const MOTION = 'transition-colors duration-150 ease-emphasized motion-reduce:transition-none';
const TEXT_BUTTON = buttonClass('quiet', 'md');
const TONAL_BUTTON = buttonClass('tonal', 'md');
// The footer's button may wrap its label: at phone width it is the full row.
const FOOTER_BUTTON = `${buttonClass('filled', 'lg')} w-full !h-auto min-h-11 py-2 text-center !whitespace-normal sm:w-auto sm:!whitespace-nowrap`;
const AMBER_BUTTON = buttonClass('warn', 'md');
const QUIET_LINK = `rounded text-xs text-on-surface-variant underline underline-offset-2 hover:text-primary ${FOCUS}`;
const CHIP = `${buttonClass('quiet', 'sm')} tabular-nums`;

// Item, Ordered, Already in, Still owed, Count, Lot. One template for the column
// heads and every row, so the numbers line up down the sheet.
const LINE_GRID =
  'md:grid-cols-[minmax(0,2.2fr)_repeat(3,minmax(4rem,0.7fr))_minmax(9rem,1.1fr)_minmax(0,1.4fr)]';

const CARRIERS: { value: ShippingCarrier; label: string }[] = [
  { value: 'ups', label: 'UPS' },
  { value: 'fedex', label: 'FedEx' },
  { value: 'usps', label: 'USPS' },
  { value: 'dhl', label: 'DHL' },
  { value: 'amazon', label: 'Amazon' },
  { value: 'other', label: 'Another carrier' },
];

const MAX_BOXES = 50;
const NUMBER_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const newRequestId = () => `rd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** A whole count above zero, or null. "08" is 8; "1.5", "-2" and "" are not counts. */
function parseCount(raw: string): number | null {
  if (!/^\s*\d+\s*$/.test(raw)) return null;
  const n = parseInt(raw, 10);
  return n > 0 ? n : null;
}

/** A whole number at or above zero, for the box cells where 0 is allowed. */
function parseCell(raw: string): number | null {
  if (!/^\s*\d+\s*$/.test(raw)) return null;
  return parseInt(raw, 10);
}

/** What a person calls the line, never the em-dash placeholder. */
function nameOf(line: OrderReceiveLine): string {
  const label = itemLabel(line);
  if (label && label !== '—') return label;
  return line.sku || 'This item';
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Received net of returns: what the order's balance is measured against. */
const netIn = (l: OrderReceiveLine) => l.quantity_received - (l.quantity_returned || 0);
const stillOwed = (l: OrderReceiveLine) => Math.max(0, l.quantity_expected - netIn(l));
const isOpenStatus = (l: OrderReceiveLine) => l.status === 'pending' || l.status === 'partial';

/** Even split with the remainder on the last box. */
function evenSplit(total: number, boxes: number): string[] {
  const base = Math.floor(total / boxes);
  const cells = Array.from({ length: boxes }, () => base);
  cells[boxes - 1] += total - base * boxes;
  return cells.map(String);
}

/**
 * The note on the order line, unless it only repeats what is already on screen.
 * On a scanned or imported order the note is often the vendor's description
 * echoing the SKU or the name.
 */
function lineNoteOf(line: OrderReceiveLine): string {
  const raw = (line.notes || '').trim();
  if (!raw) return '';
  const restates = [line.sku, line.part_description, itemLabel(line)]
    .filter(Boolean)
    .some((s) => String(s).trim().toLowerCase() === raw.toLowerCase());
  return restates ? '' : raw;
}

/**
 * The revision the order asked for, when the item's own name does not already
 * say it. Token equality, not a substring: "rev 1" sits inside "rev 1.2".
 */
function orderedRevisionOf(line: OrderReceiveLine): string {
  const rev = (line.revision || '').trim();
  if (!rev) return '';
  const named = line.variant_detail || line.display_name || '';
  const namedRev = /\brev ([^,)]+)/i.exec(named)?.[1]?.trim() || '';
  return namedRev.toLowerCase() === rev.toLowerCase() ? '' : rev;
}

const MinusIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
    <path d="M3 7.25h10v1.5H3z" fill="currentColor" />
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
    <path d="M7.25 3h1.5v4.25H13v1.5H8.75V13h-1.5V8.75H3v-1.5h4.25z" fill="currentColor" />
  </svg>
);
const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    viewBox="0 0 16 16"
    width="14"
    height="14"
    fill="none"
    aria-hidden="true"
    className={`transition-transform duration-150 ease-emphasized motion-reduce:transition-none ${open ? 'rotate-90' : ''}`}
  >
    <path d="M6 3.5 10.5 8 6 12.5 4.95 11.45 8.4 8 4.95 4.55z" fill="currentColor" />
  </svg>
);

// ── Per-line state ───────────────────────────────────────────────────────────

interface LineDraft {
  count: string;
  lot: string;
  rejecting: boolean;
  rejectedQty: string;
  rejectionReason: string;
  rejectionResolution: 'return_to_vendor' | 'scrap';
  /** The browser could not read what was typed (a lone "-" in a number field reads as ""). */
  countBadInput: boolean;
  /** Hand-edited box cells, valid only while the box count they were typed for holds. */
  splitEdits: { boxCount: number; cells: string[] } | null;
}

const blankDraft = (lot = ''): LineDraft => ({
  count: '',
  lot,
  rejecting: false,
  rejectedQty: '',
  rejectionReason: '',
  rejectionResolution: 'return_to_vendor',
  countBadInput: false,
  splitEdits: null,
});

/** Something typed that is not a count: "1.5", "-2", a dash. "0" and "" are blank, not typos. */
const isCountTypo = (d: LineDraft) => d.countBadInput || (d.count.trim() !== '' && !/^\s*\d+\s*$/.test(d.count));

/** A value for a screen reader, held back until typing has stopped for 700ms. */
function useSettled<T>(value: T, initial: T): T {
  const [settled, setSettled] = useState<T>(initial);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), 700);
    return () => clearTimeout(t);
  }, [value]);
  return settled;
}

function cellsFor(draft: LineDraft, count: number, boxCount: number): string[] {
  if (draft.splitEdits && draft.splitEdits.boxCount === boxCount) return draft.splitEdits.cells;
  return evenSplit(count, boxCount);
}

function splitSum(cells: string[]): number | null {
  let sum = 0;
  for (const c of cells) {
    const n = parseCell(c);
    if (n === null) return null;
    sum += n;
  }
  return sum;
}

/**
 * Why a line's box split cannot go up, as one sentence, or null. The split is of
 * the TOTAL that arrived; the server takes the rejected units out of box 1, so
 * box 1 has to hold at least that many.
 */
function splitIssue(draft: LineDraft, boxCount: number): string | null {
  const q = parseCount(draft.count);
  if (q === null || boxCount <= 1) return null;
  const cells = cellsFor(draft, q, boxCount);
  const sum = splitSum(cells);
  if (sum === null) return 'Every box needs a whole number. Put 0 in a box that holds none.';
  if (sum !== q) return `These add up to ${sum}, not ${q}. Make the boxes match the count.`;
  const bad = draft.rejecting ? parseCount(draft.rejectedQty) : null;
  const first = parseCell(cells[0]) ?? 0;
  if (bad !== null && bad < q && first < bad) {
    return `Box 1 has to hold at least the ${bad} that are not acceptable.`;
  }
  return null;
}

// ── The mirror ───────────────────────────────────────────────────────────────

// `held` is the part of the count going to QC Hold. Those units count as
// received, but the return they open nets them back out of the line, so a line
// with a rejection stays open for them (purchase-returns.md). The sentence has
// to say so, or it promises a close the server will not write.
function mirrorSentence(line: OrderReceiveLine, q: number, held = 0): string {
  const totalAfter = netIn(line) + q;
  const head = `Brings this line to ${totalAfter} of ${line.quantity_expected}.`;
  if (totalAfter > line.quantity_expected) {
    const over = `${head} That is ${totalAfter - line.quantity_expected} more than the order asked for.`;
    return held > 0 ? `${over} ${held} go to QC Hold with a return open, so it stays open for them.` : over;
  }
  if (held > 0) {
    const owed = line.quantity_expected - totalAfter;
    return owed > 0
      ? `${head} ${owed} still owed, plus the ${held} at QC Hold, so it stays open.`
      : `${head} ${held} go to QC Hold with a return open, so it stays open for them.`;
  }
  if (totalAfter === line.quantity_expected) return `${head} Closes the line.`;
  return `${head} ${line.quantity_expected - totalAfter} still owed, so it stays open.`;
}

const Mirror: React.FC<{ line: OrderReceiveLine; count: string; typo: boolean; held?: number }> = ({
  line,
  count,
  typo,
  held = 0,
}) => {
  // Two nodes, one sentence. The visible one follows every keystroke and is
  // aria-hidden; the announced one waits for typing to settle, or a polite
  // region queues a confident false claim for every prefix of the number.
  const settled = useSettled(count, '');
  const settledHeld = useSettled(held, 0);

  const q = typo ? null : parseCount(count);
  const settledQ = parseCount(settled);
  const totalAfter = q === null ? 0 : netIn(line) + q;
  const expected = line.quantity_expected;

  return (
    <div className="col-span-full">
      <p className="text-xs leading-snug min-h-[1rem]" aria-hidden="true" data-testid={`mirror-${line.id}`}>
        {typo ? (
          <span className="text-on-surface-variant">Not sent until the count is a whole number.</span>
        ) : q === null ? (
          <span className="text-on-surface-variant">Not in this delivery. Stays open.</span>
        ) : (
          <>
            <span className="text-on-surface-variant">
              Brings this line to{' '}
              <span className="font-medium tabular-nums text-on-surface">{totalAfter}</span> of{' '}
              <span className="tabular-nums text-on-surface">{expected}</span>.
            </span>{' '}
            {totalAfter > expected ? (
              <span className="text-amber-300">
                That is <span className="tabular-nums">{totalAfter - expected}</span> more than the order
                asked for. If that is past the allowance, an admin is asked before anything is written.
                {held > 0 && (
                  <>
                    {' '}
                    <span className="tabular-nums">{held}</span> go to QC Hold with a return open, so it stays
                    open for them.
                  </>
                )}
              </span>
            ) : held > 0 ? (
              <span className="text-on-surface-variant">
                {expected - totalAfter > 0 ? (
                  <>
                    <span className="tabular-nums text-on-surface">{expected - totalAfter}</span> still owed, plus
                    the <span className="tabular-nums text-on-surface">{held}</span> at QC Hold, so it stays open.
                  </>
                ) : (
                  <>
                    <span className="tabular-nums text-on-surface">{held}</span> go to QC Hold with a return open,
                    so it stays open for them.
                  </>
                )}
              </span>
            ) : totalAfter === expected ? (
              <span className="text-amber-300">Closes the line.</span>
            ) : (
              <span className="text-on-surface-variant">
                <span className="tabular-nums text-on-surface">{expected - totalAfter}</span> still owed, so it
                stays open.
              </span>
            )}
          </>
        )}
      </p>
      <span className="sr-only" role="status" aria-live="polite">
        {typo || settledQ === null ? '' : mirrorSentence(line, settledQ, settledHeld)}
      </span>
    </div>
  );
};

// ── One editable line ────────────────────────────────────────────────────────

interface LineRowProps {
  line: OrderReceiveLine;
  draft: LineDraft;
  slip: PendingSlipLine | null;
  boxCount: number;
  onChange: (patch: Partial<LineDraft>) => void;
  autofocus: boolean;
}

const LineRow: React.FC<LineRowProps> = ({ line, draft, slip, boxCount, onChange, autofocus }) => {
  const name = nameOf(line);
  const owed = stillOwed(line);
  const q = parseCount(draft.count);
  const note = lineNoteOf(line);
  const orderedRevision = orderedRevisionOf(line);
  const returned = line.quantity_returned || 0;

  // The slip's claim outranks the order balance when a packing list was read.
  const fill =
    slip && slip.quantity_outstanding > 0
      ? { n: slip.quantity_outstanding, label: `All ${slip.quantity_outstanding} on the slip` }
      : owed > 0
        ? { n: owed, label: `All ${owed} arrived` }
        : null;

  const bad = parseCount(draft.rejectedQty);
  const rejectedSplit = draft.rejecting && q !== null && bad !== null && bad < q ? { ok: q - bad, bad } : null;

  const typo = isCountTypo(draft);
  const cells = q !== null && boxCount > 1 ? cellsFor(draft, q, boxCount) : null;
  const issue = splitIssue(draft, boxCount);
  // Announced once the cells stop changing, like the mirror.
  const settledIssue = useSettled(issue, null);

  const setCell = (i: number, value: string) => {
    if (!cells) return;
    const next = [...cells];
    next[i] = value;
    onChange({ splitEdits: { boxCount, cells: next } });
  };

  return (
    <li
      data-line-id={line.id}
      className={`grid grid-cols-1 gap-x-4 gap-y-2 rounded-lg px-3 py-3 sm:px-4 ${LINE_GRID} ${
        q !== null ? 'bg-surface-container-high' : 'bg-surface-container'
      } kbd-within:ring-2 kbd-within:ring-primary/40 ${MOTION}`}
    >
      {/* What it is */}
      <div className="min-w-0">
        <ItemName size="md" showSku item={line} />
        {orderedRevision && (
          <p className="mt-1 text-xs text-on-surface-variant">
            Ordered at revision <span className="text-on-surface">{orderedRevision}</span>
          </p>
        )}
        {note && (
          <p className="mt-1 whitespace-pre-wrap text-xs text-on-surface-variant">
            On the order line: <span className="text-on-surface">{note}</span>
          </p>
        )}
        {line.receipt_items.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[11px]">
            <span className="text-on-surface-variant">Came in:</span>
            {line.receipt_items.map((it) => {
              const rejected = it.disposition === 'rejected';
              return (
                <Link
                  key={it.id}
                  to={`/receiving/item/${it.id}`}
                  className={`rounded-full px-2 py-0.5 tabular-nums ${FOCUS} ${
                    rejected
                      ? 'bg-amber-500/15 text-amber-200'
                      : 'bg-surface-container-highest text-on-surface-variant hover:text-on-surface'
                  }`}
                  aria-label={
                    rejected
                      ? `${it.quantity_received} rejected on ${formatTimestamp(it.received_at, 'MMM D')}, box ${it.barcode}, held at QC`
                      : `${it.quantity_received} received ${formatTimestamp(it.received_at, 'MMM D')}, box ${it.barcode}`
                  }
                >
                  {it.quantity_received}
                  <span className="ml-1 opacity-80">
                    {rejected ? 'rejected' : formatTimestamp(it.received_at, 'MMM D')}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* The order's numbers. A row of three on a phone, three columns on a desk. */}
      <div className="flex gap-4 text-xs text-on-surface-variant md:contents">
        <div className="tabular-nums md:pt-1 md:text-right md:text-sm md:text-on-surface">
          <span className="md:sr-only">Ordered </span>
          <b className="font-medium text-on-surface">{line.quantity_expected}</b>
        </div>
        <div className="tabular-nums md:pt-1 md:text-right md:text-sm">
          <span className="md:sr-only">Already in </span>
          <b className="font-medium text-on-surface md:font-normal md:text-on-surface-variant">{netIn(line)}</b>
          {returned > 0 && <span className="block text-[11px]">{returned} sent back</span>}
        </div>
        <div className="tabular-nums md:pt-1 md:text-right md:text-base">
          <span className="md:sr-only">Still owed </span>
          <b className="font-semibold text-on-surface">{owed}</b>
        </div>
      </div>

      {/* The count. The one field only the person at the boxes can fill. */}
      <div className="min-w-0">
        <label htmlFor={`count-${line.id}`} className="sr-only">
          How many {name} turned up?
        </label>
        <Field
          id={`count-${line.id}`}
          size="lg"
          className="text-lg"
          error={typo}
          type="number"
          inputMode="numeric"
          min={0}
          value={draft.count}
          aria-describedby={typo ? `count-typo-${line.id}` : undefined}
          onChange={(e) =>
            onChange({ count: e.target.value, countBadInput: !!e.target.validity?.badInput })
          }
          placeholder="Count them"
          // A wheel over a focused number input steps its value and swallows the
          // scroll. Blur, so the page scrolls and the count stays what was typed.
          onWheel={(e) => e.currentTarget.blur()}
          data-drawer-autofocus={autofocus ? '' : undefined}
        />
        <Reveal show={typo}>
          <div id={`count-typo-${line.id}`} className="mt-1">
            <Alert tone="error" variant="text">Whole numbers only</Alert>
          </div>
        </Reveal>
        <Reveal show={!!fill}>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <button type="button" className={CHIP} onClick={() => fill && onChange({ count: String(fill.n) })}>
              {fill?.label}
            </button>
          </div>
        </Reveal>
        {slip && (
          <p className="mt-1 text-[11px] leading-snug text-on-surface-variant">
            {slip.slip_number ? `Slip ${slip.slip_number}` : 'The packing list'} says{' '}
            <span className="tabular-nums text-on-surface">{slip.quantity_shipped}</span>
            {slip.quantity_outstanding !== slip.quantity_shipped && (
              <>
                , <span className="tabular-nums text-on-surface">{slip.quantity_outstanding}</span> of them not
                yet boxed
              </>
            )}
            . Change it to what you actually count.
          </p>
        )}
      </div>

      {/* Lot, and the rare path */}
      <div className="min-w-0">
        <label htmlFor={`lot-${line.id}`} className="sr-only">
          Lot number for {name}
        </label>
        <Field
          id={`lot-${line.id}`}
          thumb
          value={draft.lot}
          onChange={(e) => onChange({ lot: e.target.value })}
          placeholder="Lot, off the bag"
        />
        {!draft.rejecting && (
          <button type="button" className={`mt-1.5 ${QUIET_LINK}`} onClick={() => onChange({ rejecting: true })}>
            Some of these are not acceptable
          </button>
        )}
      </div>

      <Mirror line={line} count={draft.count} typo={typo} held={draft.rejecting && bad !== null ? Math.min(bad, q ?? 0) : 0} />

      {draft.rejecting && (
        <Panel
          level={3}
          padding="sm"
          title="Not acceptable"
          className="col-span-full space-y-3 ring-1 ring-inset ring-amber-500/30"
          actions={(
            <button
              type="button"
              className={`${buttonClass('quiet', 'sm')} shrink-0`}
              onClick={() => onChange({ rejecting: false, rejectedQty: '', rejectionReason: '' })}
            >
              Cancel
            </button>
          )}
        >
          <p className="text-[11px] leading-relaxed text-on-surface-variant">
            These still count as received, because the vendor shipped them. They go to QC Hold instead of
            stock, and a return is opened.
          </p>
          <div className="grid gap-3 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)_minmax(0,14rem)]">
            <label className="block">
              <span className={modalLabel}>How many are bad</span>
              <Field
                id={`bad-${line.id}`}
                thumb
                type="number"
                inputMode="numeric"
                min={1}
                value={draft.rejectedQty}
                onChange={(e) => onChange({ rejectedQty: e.target.value })}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="0"
              />
            </label>
            <label className="block">
              <span className={modalLabel}>What is wrong with them</span>
              <Field
                id={`reason-${line.id}`}
                thumb
                value={draft.rejectionReason}
                onChange={(e) => onChange({ rejectionReason: e.target.value })}
                placeholder="Bent pins, water damage, dead at the flash station"
              />
            </label>
            <label className="block">
              <span className={modalLabel}>What happens to them</span>
              <Select
                label="What happens to them"
                value={draft.rejectionResolution}
                onChange={(key) => onChange({ rejectionResolution: key as 'return_to_vendor' | 'scrap' })}
                options={[
                  { key: 'return_to_vendor', label: 'Send back to the vendor' },
                  { key: 'scrap', label: 'Scrap them' },
                ]}
              />
            </label>
          </div>
          <p className="text-xs text-on-surface-variant">
            {rejectedSplit
              ? `${rejectedSplit.ok} into stock, ${rejectedSplit.bad} to QC Hold.`
              : 'Enter how many, out of the count above.'}
          </p>
        </Panel>
      )}

      {cells && q !== null && (
        <div className="col-span-full">
          <div className="mb-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className={`${modalLabel} mb-0`}>
              How the {q} split across the {boxCount} boxes
            </span>
            {draft.splitEdits && (
              <button type="button" className={QUIET_LINK} onClick={() => onChange({ splitEdits: null })}>
                Split evenly again
              </button>
            )}
          </div>
          <div
            role="group"
            aria-label={`How the ${q} ${name} split across the boxes`}
            className="grid grid-cols-[repeat(auto-fill,minmax(6.5rem,1fr))] gap-2"
          >
            {cells.map((c, i) => (
              <label key={i} className="block rounded-md bg-surface-container-low px-2 pb-2 pt-1.5">
                <span className="block text-[11px] text-on-surface-variant">Box {i + 1}</span>
                <Field
                  thumb
                  className="mt-0.5"
                  error={parseCell(c) === null}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={c}
                  onChange={(e) => setCell(i, e.target.value)}
                  onWheel={(e) => e.currentTarget.blur()}
                />
              </label>
            ))}
          </div>
          <Reveal show={!!issue}>
            <p className="mt-1.5 text-xs text-red-300" aria-hidden="true">
              {issue}
            </p>
          </Reveal>
          <span className="sr-only" role="status" aria-live="polite">
            {settledIssue || ''}
          </span>
        </div>
      )}
    </li>
  );
};

// ── A line nothing more is expected on ───────────────────────────────────────

const ClosedRow: React.FC<{ line: OrderReceiveLine; onReceiveMore: (() => void) | null }> = ({
  line,
  onReceiveMore,
}) => (
  <li className={`grid grid-cols-1 items-start gap-x-4 gap-y-1 rounded-lg bg-surface-container-low px-3 py-2.5 sm:px-4 ${LINE_GRID}`}>
    <div className="min-w-0 text-on-surface-variant">
      <ItemName size="sm" showSku item={line} />
    </div>
    <div className="flex gap-4 text-xs text-on-surface-variant md:contents">
      <div className="tabular-nums md:text-right md:text-sm">
        <span className="md:sr-only">Ordered </span>
        {line.quantity_expected}
      </div>
      <div className="tabular-nums md:text-right md:text-sm">
        <span className="md:sr-only">Already in </span>
        {netIn(line)}
      </div>
      <div className="tabular-nums md:text-right md:text-sm">
        <span className="md:sr-only">Still owed </span>
        {stillOwed(line)}
      </div>
    </div>
    <div className="text-xs text-on-surface-variant md:col-span-2">
      {line.status === 'cancelled' ? (
        <span>Cancelled. Nothing more is expected.</span>
      ) : onReceiveMore ? (
        <button
          type="button"
          className={`${buttonClass('quiet', 'sm')} -ml-3`}
          onClick={onReceiveMore}
          aria-label={`Receive more ${nameOf(line)}, which is already received in full`}
        >
          Receive more
        </button>
      ) : line.status === 'received' ? (
        <span>Received in full.</span>
      ) : null}
    </div>
  </li>
);

// ── The page ─────────────────────────────────────────────────────────────────

type LoadState = 'loading' | 'ready' | 'notfound' | 'error';

interface OverState {
  refusal: OverReceiptRefusal;
  /** The exact body that was refused, received_as included, for the resubmit. */
  body: ReceiveDeliveryRequest;
}

const ReceiveOrderPage: React.FC = () => {
  const { poId, lineId } = useParams<{ poId?: string; lineId?: string }>();
  const location = useLocation();
  const history = useHistory();
  const toast = useToast();
  const deliveryApi = useReceiveDeliveryApi();
  const user = useUserStore((s) => s.user);
  const isAdmin = !!(user?.isadmin || user?.can_admin);
  const canViewPO = !!(user?.isadmin || user?.can_admin || user?.can_procurement);
  const headerless = !poId && !!lineId;
  const targetLineId = useMemo(() => new URLSearchParams(location.search).get('line'), [location.search]);

  const [load, setLoad] = useState<LoadState>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [lines, setLines] = useState<OrderReceiveLine[]>([]);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [slipByLine, setSlipByLine] = useState<Record<string, PendingSlipLine>>({});
  const [slipsLoaded, setSlipsLoaded] = useState(false);
  const [reopened, setReopened] = useState<Set<string>>(new Set());
  const [foldOpen, setFoldOpen] = useState(false);

  // The delivery's own fields.
  const [slipNumber, setSlipNumber] = useState('');
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [boxCount, setBoxCount] = useState(1);
  const [shippingOpen, setShippingOpen] = useState(false);
  const [carrier, setCarrier] = useState<ShippingCarrier | ''>('');
  const [tracking, setTracking] = useState('');
  const [receivedAt, setReceivedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [printItemStickers, setPrintItemStickers] = useState(false);

  // The press.
  const [submitting, setSubmitting] = useState(false);
  const [pendingBody, setPendingBody] = useState<ReceiveDeliveryRequest | null>(null);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [over, setOver] = useState<OverState | null>(null);
  // Idempotency for the whole delivery. Kept across a retry after a lost
  // connection (the first attempt may have landed); replaced after any answer
  // the server gave, because every refusal writes nothing.
  const clientRequestId = useRef(newRequestId());
  const focusedOnce = useRef(false);

  // A new order or line is a new delivery. The two routes render this component
  // in the same slot, so React keeps the instance across them (the headerless
  // redirect included) and nothing typed for one order may carry to the next.
  useEffect(() => {
    setRedirectTo(null);
    setLines([]);
    setDrafts({});
    setSlipByLine({});
    setSlipsLoaded(false);
    setReopened(new Set());
    setFoldOpen(false);
    setSlipNumber('');
    setBoxCount(1);
    setShippingOpen(false);
    setCarrier('');
    setTracking('');
    setReceivedAt('');
    setNotes('');
    setPrintItemStickers(false);
    setPendingBody(null);
    setIdentityError(null);
    setOver(null);
    focusedOnce.current = false;
    clientRequestId.current = newRequestId();
  }, [poId, lineId]);

  // ── Loading ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoad('loading');
      try {
        let rows: OrderReceiveLine[];
        if (headerless) {
          const one = await deliveryApi.line(lineId as string);
          if (one.purchase_order_id) {
            if (!cancelled) setRedirectTo(`/receiving/order/${one.purchase_order_id}?line=${one.id}`);
            return;
          }
          rows = [{ ...one, receipt_items: one.receipt_items || [] }];
        } else {
          rows = await deliveryApi.orderLines(poId as string);
        }
        if (cancelled) return;
        setLines(rows);
        setDrafts((prev) => {
          const next: Record<string, LineDraft> = {};
          for (const r of rows) next[r.id] = prev[r.id] || blankDraft();
          return next;
        });
        // A link to a line already received in full is a "Receive more": open it.
        const target = rows.find((r) => r.id === targetLineId);
        if (target && target.status === 'received') {
          setReopened((s) => new Set(s).add(target.id));
          setFoldOpen(true);
        }
        if (!rows.some(isOpenStatus)) setFoldOpen(true);
        setLoad('ready');
      } catch (err: any) {
        if (cancelled) return;
        setLoad(err?.status === 404 ? 'notfound' : 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poId, lineId, reloadKey]);

  // Receivable locations, as the drawer: one warehouse is preselected, two are not.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await deliveryApi.receivableLocations();
        if (cancelled) return;
        setLocations(rows);
        setLocationsLoaded(true);
        const warehouses = rows.filter((l) => l.type !== 'vendor');
        if (warehouses.length === 1) setLocationId((cur) => cur || String(warehouses[0].id));
      } catch (err: any) {
        if (cancelled) return;
        setLocationsLoaded(true);
        toast.error(err?.message || 'The locations did not load. Reload the page to try again.');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The packing slip's claims, best effort: a page that refuses to open because
  // the slip feed failed is worse than one without the chips.
  useEffect(() => {
    if (load !== 'ready' || lines.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const pending = await deliveryApi.pendingSlipLines();
        if (cancelled) return;
        const ids = new Set(lines.map((l) => l.id));
        const byLine: Record<string, PendingSlipLine> = {};
        for (const p of pending) {
          if (!ids.has(p.purchase_order_line_id) || byLine[p.purchase_order_line_id]) continue;
          if (p.quantity_outstanding > 0) byLine[p.purchase_order_line_id] = p;
        }
        setSlipByLine(byLine);
        setSlipsLoaded(true);
        const first = Object.values(byLine).find((p) => p.slip_number);
        if (first?.slip_number) setSlipNumber((cur) => cur || (first.slip_number as string));
        // The slip's lot rides into an empty lot field; it is a label, not a count.
        setDrafts((prev) => {
          const next = { ...prev };
          for (const [id, p] of Object.entries(byLine)) {
            if (p.lot_number && next[id] && !next[id].lot) next[id] = { ...next[id], lot: p.lot_number };
          }
          return next;
        });
      } catch {
        // Context, not the form.
        if (!cancelled) setSlipsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, lines]);

  // ── Derived ──
  const first = lines[0];
  const poStatus = first?.po_status ?? null;
  const poNumber = headerless ? first?.purchase_order_ref || null : first?.po_number || first?.purchase_order_ref || null;

  const closedReason: string | null = (() => {
    if (load !== 'ready' || !first) return null;
    if (headerless) {
      if (first.status === 'cancelled') return 'This line was cancelled, so nothing more is expected against it.';
      if ((first.line_type ?? 'stock') !== 'stock') return 'Nothing arrives in a box for a service or charge line.';
      return null;
    }
    if (poStatus === 'draft') {
      return 'This order is still a draft. Nothing can be received until it has been sent to the vendor.';
    }
    if (poStatus === 'cancelled') return 'This order was cancelled, so nothing more is expected against it.';
    if (poStatus === 'closed') return 'This order is closed, so nothing more is expected against it.';
    return null;
  })();
  const receivable = load === 'ready' && !!first && closedReason === null;

  // `receivable: false` on one line locks that line only; the order locks above.
  const lineTakesReceipt = (l: OrderReceiveLine) => l.receivable !== false && l.status !== 'cancelled';
  const openLines = useMemo(() => lines.filter((l) => isOpenStatus(l) && lineTakesReceipt(l)), [lines]);
  const foldLines = useMemo(() => lines.filter((l) => !(isOpenStatus(l) && lineTakesReceipt(l))), [lines]);
  const editable = (l: OrderReceiveLine) =>
    receivable && lineTakesReceipt(l) && (isOpenStatus(l) || reopened.has(l.id));

  const counted = useMemo(
    () => lines.filter((l) => editable(l) && parseCount(drafts[l.id]?.count ?? '') !== null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines, drafts, reopened, receivable]
  );
  const unsplitLine = useMemo(
    () => counted.find((l) => splitIssue(drafts[l.id], boxCount) !== null) || null,
    [counted, drafts, boxCount]
  );
  const leftOpen = openLines.filter((l) => !counted.includes(l));

  const blockedReason = (() => {
    if (counted.length === 0) return 'Type a count on each line that is in front of you.';
    if (!locationId) {
      return locationsLoaded && locations.length === 0
        ? 'There is nowhere to receive into yet.'
        : 'Pick a location to receive into.';
    }
    if (unsplitLine) {
      return `${nameOf(unsplitLine)}: ${splitIssue(drafts[unsplitLine.id], boxCount)}`;
    }
    return null;
  })();

  const trackedNames = counted.filter((l) => l.tracked_per_instance).map(nameOf);

  // The count to land in: the line the link named, or the first open one.
  const autofocusId = useMemo(() => {
    if (!receivable) return null;
    const target = lines.find((l) => l.id === targetLineId && editable(l));
    return target?.id ?? openLines[0]?.id ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receivable, lines, targetLineId, openLines, reopened]);

  // A page has no DrawerShell to honor data-drawer-autofocus, so this does it.
  useEffect(() => {
    if (focusedOnce.current || !autofocusId) return;
    focusedOnce.current = true;
    const t = setTimeout(() => {
      const el = document.getElementById(`count-${autofocusId}`) as HTMLInputElement | null;
      el?.focus();
      if (targetLineId) el?.scrollIntoView?.({ block: 'center' });
    }, 0);
    return () => clearTimeout(t);
  }, [autofocusId, targetLineId]);

  const locationOptions: ComboboxOption[] = useMemo(
    () =>
      locations.map((l) => ({
        id: l.id,
        label: l.name,
        sublabel: l.type === 'vendor' ? 'Holds our stock, not ours to build from' : null,
        chip: l.type === 'vendor' ? l.vendor_code || 'vendor' : null,
        group: l.type === 'vendor' ? 'Vendor premises' : 'Our locations',
      })),
    [locations]
  );
  const selectedLocation = locations.find((l) => String(l.id) === locationId) || null;

  const patchDraft = (id: string, patch: Partial<LineDraft>) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || blankDraft()), ...patch } }));

  const lineName = (id: unknown) => {
    const l = lines.find((x) => x.id === id);
    return l ? nameOf(l) : 'One line';
  };

  // ── The press ──
  const buildBody = (): ReceiveDeliveryRequest | null => {
    const out: ReceiveDeliveryLineInput[] = [];
    for (const l of counted) {
      const d = drafts[l.id];
      const q = parseCount(d.count) as number;
      const entry: ReceiveDeliveryLineInput = {
        expected_receipt_id: l.id,
        quantity_received: q,
        lot_number: d.lot.trim() || null,
        manufacture_date: null,
        notes: null,
        packing_list_line_id: slipByLine[l.id]?.id ?? null,
      };
      if (d.rejecting) {
        const bad = parseCount(d.rejectedQty);
        if (bad === null) {
          toast.error(`${nameOf(l)}: how many are bad? It has to be a whole number above zero.`);
          document.getElementById(`bad-${l.id}`)?.focus();
          return null;
        }
        if (bad >= q) {
          toast.error(
            bad === q
              ? `${nameOf(l)}: rejecting the whole count is not supported here. Receive it, then reject it from its item page.`
              : `${nameOf(l)}: you cannot reject more than arrived.`
          );
          document.getElementById(`bad-${l.id}`)?.focus();
          return null;
        }
        if (!d.rejectionReason.trim()) {
          toast.error(`${nameOf(l)}: say what is wrong with them. The vendor and the credit note both need it.`);
          document.getElementById(`reason-${l.id}`)?.focus();
          return null;
        }
        entry.quantity_rejected = bad;
        entry.rejection_reason = d.rejectionReason.trim();
        entry.rejection_resolution = d.rejectionResolution;
      }
      if (boxCount > 1) {
        entry.boxes = cellsFor(d, q, boxCount)
          .map((c, i) => ({ box_no: i + 1, quantity: parseCell(c) ?? 0 }))
          .filter((b) => b.quantity > 0);
      }
      out.push(entry);
    }
    return {
      purchase_order_id: headerless ? null : (poId as string),
      location_id: parseInt(locationId, 10),
      box_count: boxCount,
      slip_number: slipNumber.trim() || null,
      carrier: carrier || null,
      tracking_number: tracking.trim() || null,
      notes: notes.trim() || null,
      ...(isAdmin && receivedAt ? { received_at: receivedAt } : {}),
      client_request_id: clientRequestId.current,
      lines: out,
    };
  };

  const handlePress = () => {
    if (blockedReason || submitting) return;
    const body = buildBody();
    if (!body) return;
    setIdentityError(null);
    setPendingBody(body);
  };

  const submit = async (body: ReceiveDeliveryRequest) => {
    setSubmitting(true);
    try {
      const result = await deliveryApi.receive(body);
      const n = body.lines.length;
      // The rejected units sit in a QC box of their own; that is not a box that arrived.
      const accepted = (result.boxes || []).filter((b) => b.disposition !== 'rejected').length;
      const m = accepted || body.box_count;
      if (result.duplicate) {
        toast.info('This delivery was already received. Its stickers are opening again.');
      } else {
        toast.success(`Received ${plural(n, 'line', 'lines')} into ${plural(m, 'box', 'boxes')}`);
      }
      openAuthedPdf(result.label_pdf_url).catch(() => {
        toast.warning('Received, but the box stickers did not open. Reprint them from the Received tab.');
      });
      if (printItemStickers && result.item_labels_url) {
        openAuthedPdf(result.item_labels_url).catch(() => {
          toast.warning('Received, but the item stickers did not open. Print them from the box page.');
        });
      }
      setSubmitting(false);
      setPendingBody(null);
      history.push('/receiving');
    } catch (err: any) {
      // Only a 4xx is an answer that wrote nothing. A 5xx (Heroku's 503 on a slow
      // receive) or a dropped connection may have landed, so the retry keeps the
      // id and the server replays instead of receiving twice.
      const answered = typeof err?.status === 'number' && err.status >= 400 && err.status < 500;
      if (answered) clientRequestId.current = newRequestId();

      const receiverError = receiverErrorMessage(err);
      if (receiverError) {
        // Nothing was received; the identity step stays open for a correction.
        const { received_as: _drop, ...withoutIdentity } = body;
        setPendingBody({ ...withoutIdentity, client_request_id: clientRequestId.current });
        setIdentityError(receiverError);
        setSubmitting(false);
        return;
      }
      // Submitting off first, so the identity step's focus return lands on an
      // enabled button, and the over dialog then records it as where to go back to.
      setSubmitting(false);
      setPendingBody(null);

      const refusal = overReceiptRefusal(err);
      if (refusal) {
        setOver({ refusal, body });
        return;
      }

      const code = errorCode(err);
      const detail = errorBody(err) || {};
      if (code === 'order_not_open') {
        toast.error(
          `This order is ${String(detail.po_status || 'not open').replace(/_/g, ' ')} now, so nothing can be received against it. Reload to see where it stands.`
        );
      } else if (code === 'expected_receipt_cancelled') {
        toast.error(
          `${lineName(detail.expected_receipt_id)} was cancelled while you were counting. Clear its count and receive the rest.`
        );
      } else if (code === 'sku_required_at_receive') {
        toast.error(
          `${lineName(detail.expected_receipt_id)} has no catalog item yet. Someone who buys has to set one on the order before it can be received. Clear its count to receive the rest.`
        );
      } else if (detail.reason === 'split_rejected_first_box') {
        toast.error(
          `${lineName(detail.expected_receipt_id)}: box 1 has to hold the units that are not acceptable. Move them into box 1 and press Receive again.`
        );
      } else if (code === 'location_required') {
        toast.error('Pick a location to receive into.');
      } else if (!answered) {
        toast.error(
          typeof err?.status === 'number' && err.status >= 500
            ? 'The server did not finish answering. Press Receive again: it will not be received twice.'
            : 'The connection dropped before an answer came back. Press Receive again: it will not be received twice.'
        );
      } else {
        toast.error(`${err?.message || 'The delivery did not go in.'} Nothing was written.`);
      }
    }
  };

  const resubmitFromOver = (mode: 'approve' | 'drop') => {
    if (!over) return;
    const ids = over.refusal.lines.map((l) => l.expected_receipt_id);
    const base = { ...over.body, client_request_id: clientRequestId.current };
    setOver(null);
    if (mode === 'approve') {
      submit({ ...base, approve_over_receipt: ids });
    } else {
      submit({ ...base, lines: base.lines.filter((l) => !ids.includes(l.expected_receipt_id)) });
    }
  };

  const goBack = () => {
    if (history.length > 1) history.goBack();
    else history.push('/receiving');
  };

  // ── Render ──
  if (redirectTo) return <Redirect to={redirectTo} />;

  const help = (
    <>
      <p>
        This page receives one delivery against this order: every line that is in the boxes, in one press, with
        one set of stickers.
      </p>
      <p className="font-medium text-on-surface">Count what is in front of you</p>
      <p>
        Nothing is filled in for you. Type what you count on each line. A line you leave blank is not in this
        delivery and stays open for the next one. The &ldquo;All 200 arrived&rdquo; button types the whole balance
        for you, and it only counts when you press it.
      </p>
      <p className="font-medium text-on-surface">Read the sentence under each line</p>
      <p>
        Before you press Receive, each line says what your number does to it: how far it brings the line, whether
        it closes it, and whether it is more than the order asked for.
      </p>
      <p className="font-medium text-on-surface">The delivery goes in together, or not at all</p>
      <p>
        If any line is more than the order allows, nothing is written. An admin can approve those lines in one
        press. Anyone else can receive the other lines now and leave those boxes for an admin.
      </p>
      <p className="font-medium text-on-surface">You say who you are once</p>
      <p>
        Your name and PIN are asked once per press, and go on every box and every item in this delivery.
      </p>
      <p className="font-medium text-on-surface">More than one box</p>
      <p>
        Set &ldquo;Arrived in&rdquo; to the number of boxes and say how each line splits across them. Each box
        gets its own sticker, listing what is inside.
      </p>
    </>
  );

  const titleText = poNumber ? `Receive ${poNumber}` : 'Receive this line';

  if (load === 'loading') {
    return (
      <PageLayout kind="record" className="pt-4" header={(
        <PageHeader className="mb-5" back={{ to: "/receiving", label: "Receiving" }} title={headerless ? 'Receive this line' : 'Receive this order'} help={help} helpTitle="About receiving an order" />
      )}>
        <SkeletonGroup label="Loading the order" className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20 rounded-panel" />
          ))}
        </SkeletonGroup>
      </PageLayout>
    );
  }

  if (load === 'notfound' || load === 'error') {
    return (
      <PageLayout kind="record" className="pt-4" header={<BackLink to="/receiving" label="Receiving" className="mb-5" />}>
        <EmptyState
          className="max-w-xl"
          title={load === 'notfound'
            ? headerless
              ? 'There is no order line with this id.'
              : 'There is no order with this id.'
            : 'This order did not load.'}
          action={load === 'error' ? (
            <button type="button" className={TONAL_BUTTON} onClick={() => setReloadKey((k) => k + 1)}>
              Try again
            </button>
          ) : undefined}
        >
          {load === 'notfound'
            ? 'The link may be old. Find the order in the Receiving Hub instead.'
            : 'Check your connection and try again.'}
        </EmptyState>
      </PageLayout>
    );
  }

  const eta = (() => {
    const dates = openLines.map((l) => l.expected_arrival_date).filter(Boolean) as string[];
    return dates.sort()[0] || null;
  })();
  const vendor = first?.vendor || null;
  const lede = lines.length
    ? [
        vendor,
        eta ? `due ${formatEventDate(eta, 'MMM D')}` : null,
      ]
        .filter(Boolean)
        .join(', ') +
      `${vendor || eta ? '. ' : ''}${openLines.length} of ${plural(lines.length, 'line', 'lines')} still expected.`
    : null;

  const n = counted.length;
  const buttonLabel = submitting
    ? 'Receiving…'
    : n === 0
      ? 'Receive & print'
      : `Receive ${plural(n, 'line', 'lines')} & print${boxCount > 1 ? ` ${boxCount}` : ''}`;

  const renderEditable = (l: OrderReceiveLine) => (
    <LineRow
      key={l.id}
      line={l}
      draft={drafts[l.id] || blankDraft()}
      slip={slipByLine[l.id] || null}
      boxCount={boxCount}
      onChange={(patch) => patchDraft(l.id, patch)}
      autofocus={l.id === autofocusId}
    />
  );

  const columnHeads = (
    <div aria-hidden="true" className={`hidden gap-x-4 px-4 pb-1 md:grid ${LINE_GRID}`}>
      {['Item', 'Ordered', 'Already in', 'Still owed', 'Count', 'Lot'].map((h, i) => (
        <span
          key={h}
          className={`text-[11px] font-medium text-on-surface-variant ${i >= 1 && i <= 3 ? 'text-right' : ''}`}
        >
          {h}
        </span>
      ))}
    </div>
  );

  const foldLabel = (() => {
    const received = foldLines.filter((l) => l.status !== 'cancelled').length;
    const cancelled = foldLines.length - received;
    if (cancelled === 0) return `Received in full (${received})`;
    if (received === 0) return `Cancelled (${cancelled})`;
    return `Received in full or cancelled (${foldLines.length})`;
  })();

  return (
    <PageLayout kind="record" className="pt-4" header={(
      <PageHeader
        className="mb-5"
        back={{ to: "/receiving", label: "Receiving" }}
        title={titleText}
        help={help}
        helpTitle="About receiving an order"
        lede={lede}
        meta={
          poStatus === 'pending_approval' ? (
            <>
              <Tag tone="warn">Pending approval</Tag>
              <span className="text-xs text-on-surface-variant">
                Not approved yet. You can still receive: approval covers the paperwork, not the goods.
              </span>
            </>
          ) : undefined
        }
        actions={
          <>
            {receivable && slipsLoaded && Object.keys(slipByLine).length === 0 && (
              <SecondaryAction variant="text" to="/receiving/packing-list">
                Read the packing list
              </SecondaryAction>
            )}
            {!headerless && canViewPO && poId && (
              <SecondaryAction variant="text" to={`/procurement/po/${poId}`}>
                Open the order
              </SecondaryAction>
            )}
          </>
        }
      />
    )}>
    <div className="space-y-5">
      <Reveal show={!!closedReason}>
        <Alert tone="info" live={false} title={closedReason}>
          The lines are below for reference.
        </Alert>
      </Reveal>

      {lines.length === 0 && (
        <EmptyState
          title="This order has nothing to receive."
          action={canViewPO && poId ? (
            <Link to={`/procurement/po/${poId}`} className={TONAL_BUTTON}>
              Open the order
            </Link>
          ) : undefined}
        >
          It has no stock lines. Services and charges never arrive in a box.
        </EmptyState>
      )}

      {receivable && (
        <Panel label="This delivery">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className={modalLabel}>Packing slip</span>
              <Field
                thumb
                value={slipNumber}
                maxLength={100}
                onChange={(e) => setSlipNumber(e.target.value)}
                placeholder="Slip number, if there is one"
              />
            </label>

            <div className="min-w-0">
              <EntityCombobox
                label="Location"
                value={locationId}
                options={locationOptions}
                onSelect={(o) => setLocationId(o ? String(o.id) : '')}
                placeholder={locations.length ? 'Type to search' : 'No locations yet'}
                disabled={!locations.length}
                labelClassName={modalLabel}
                emptyMessage="Nothing matches that."
                noOptionsMessage="No locations to receive into."
              />
              <Reveal show={locationsLoaded && locations.length === 0}>
                <p className="mt-1.5 text-xs leading-snug text-primary/90">
                  Nowhere to receive into yet. An admin needs to add a warehouse under Settings, Locations.
                </p>
              </Reveal>
              <Reveal show={selectedLocation?.type === 'vendor'}>
                <p className="mt-1.5 text-xs leading-snug text-on-surface-variant">
                  This is {selectedLocation?.vendor_name || 'a vendor'}&apos;s premises, not your shelf. Stock filed
                  here does not count toward what you can build.
                </p>
              </Reveal>
            </div>

            <div>
              <span className={modalLabel} id="arrived-in-label">
                Arrived in
              </span>
              <div
                role="group"
                aria-labelledby="arrived-in-label"
                className="inline-flex items-center gap-0.5 rounded-md bg-surface-container-highest p-0.5 shadow-[inset_0_1px_2px_rgba(0,0,0,.35)]"
              >
                <button
                    type="button"
                    aria-label="One box fewer"
                    disabled={boxCount <= 1}
                    onClick={() => setBoxCount((b) => Math.max(1, b - 1))}
                    className={`flex h-9 w-9 items-center justify-center rounded bg-surface-container-high text-on-surface-variant shadow-[inset_0_1px_0_rgba(255,255,255,.04),inset_0_-1px_0_rgba(0,0,0,.22)] hover:text-on-surface disabled:opacity-40 ${FOCUS} ${MOTION}`}
                  >
                    <MinusIcon />
                  </button>
                <output aria-live="polite" className="min-w-[5.5rem] px-2 text-center text-sm tabular-nums text-on-surface">
                  {plural(boxCount, 'box', 'boxes')}
                </output>
                <button
                    type="button"
                    aria-label="One box more"
                    disabled={boxCount >= MAX_BOXES}
                    onClick={() => setBoxCount((b) => Math.min(MAX_BOXES, b + 1))}
                    className={`flex h-9 w-9 items-center justify-center rounded bg-surface-container-high text-on-surface-variant shadow-[inset_0_1px_0_rgba(255,255,255,.04),inset_0_-1px_0_rgba(0,0,0,.22)] hover:text-on-surface disabled:opacity-40 ${FOCUS} ${MOTION}`}
                  >
                    <PlusIcon />
                  </button>
              </div>
            </div>

            {isAdmin && (
              <label className="block">
                <span className={modalLabel}>
                  Received on
                  <span className="ml-2 normal-case tracking-normal">(blank = today)</span>
                </span>
                <Field
                  thumb
                  type="date"
                  max={todayIso()}
                  value={receivedAt}
                  onChange={(e) => setReceivedAt(e.target.value)}
                />
                <Reveal show={!!receivedAt && receivedAt !== todayIso()}>
                  <p className="mt-1.5 text-xs leading-snug text-primary/90">
                    Backdating. The stock movement is recorded on this date too, so it lands in the right month.
                  </p>
                </Reveal>
              </label>
            )}

            <label className="block sm:col-span-2">
              <span className={modalLabel}>Notes</span>
              <Field
                thumb
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional. Goes on every box of this delivery."
              />
            </label>

            {shippingOpen ? (
              <FadeIn key="shipping" className="grid gap-3 sm:col-span-2 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)]">
                <label className="block">
                  <span className={modalLabel}>Carrier</span>
                  <Select
                    label="Carrier"
                    value={carrier}
                    onChange={(key) => setCarrier(key as ShippingCarrier | '')}
                    options={[
                      { key: '', label: 'From the number' },
                      ...CARRIERS.map((c) => ({ key: c.value, label: c.label })),
                    ]}
                  />
                </label>
                <label className="block">
                  <span className={modalLabel}>Tracking number</span>
                  <Field
                    thumb
                    className="font-mono"
                    value={tracking}
                    maxLength={100}
                    onChange={(e) => setTracking(e.target.value)}
                    placeholder="Off the shipping label"
                  />
                </label>
              </FadeIn>
            ) : (
              <FadeIn key="add-shipping" className="flex items-end sm:col-span-2">
                <button type="button" className={`${QUIET_LINK} mb-2`} onClick={() => setShippingOpen(true)}>
                  Add the shipping label
                </button>
              </FadeIn>
            )}
          </div>
        </Panel>
      )}

      {lines.length > 0 && (
        <section aria-label="Lines on this order" className="space-y-2">
          <Reveal show={receivable && openLines.length === 0}>
            <Alert tone="success" live={false} title="Everything on this order has been received.">
              If more turned up than was ordered, use Receive more on the line below.
            </Alert>
          </Reveal>

          {(openLines.length > 0 || (foldOpen && foldLines.length > 0)) && columnHeads}

          {openLines.length > 0 && (
            <ul className="space-y-2">
              {openLines.map((l) =>
                receivable ? renderEditable(l) : <ClosedRow key={l.id} line={l} onReceiveMore={null} />
              )}
            </ul>
          )}

          {foldLines.length > 0 && (
            <div className="pt-2">
              <button
                type="button"
                aria-expanded={foldOpen}
                onClick={() => {
                  if (foldOpen && reopened.size > 0) {
                    // A hidden count would still go up with the delivery.
                    setDrafts((prev) => {
                      const next = { ...prev };
                      reopened.forEach((id) => {
                        next[id] = blankDraft(prev[id]?.lot || '');
                      });
                      return next;
                    });
                    setReopened(new Set());
                  }
                  setFoldOpen((o) => !o);
                }}
                className={`inline-flex items-center gap-1.5 rounded px-1 py-1 text-xs font-medium text-on-surface-variant hover:text-on-surface ${FOCUS}`}
              >
                <ChevronIcon open={foldOpen} />
                {foldLabel}
              </button>
              <Reveal show={foldOpen}>
                <ul className="mt-1 space-y-2 p-1">
                  {foldLines.map((l) =>
                    editable(l) ? (
                      renderEditable(l)
                    ) : (
                      <ClosedRow
                        key={l.id}
                        line={l}
                        onReceiveMore={
                          receivable && l.status === 'received'
                            ? () => {
                                setReopened((s) => new Set(s).add(l.id));
                                setTimeout(() => document.getElementById(`count-${l.id}`)?.focus(), 0);
                              }
                            : null
                        }
                      />
                    )
                  )}
                </ul>
              </Reveal>
            </div>
          )}
        </section>
      )}

      {receivable && (
        <footer className="sticky bottom-0 z-10 -mx-4 sm:-mx-6 2xl:-mx-8 border-t border-outline-variant bg-surface px-4 sm:px-6 2xl:px-8 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 space-y-1 text-xs text-on-surface-variant">
              <p className="text-sm" data-testid="delivery-summary">
                {n === 0 ? (
                  'Nothing counted yet.'
                ) : (
                  <>
                    Receiving <b className="font-semibold text-on-surface">{plural(n, 'line', 'lines')}</b> into{' '}
                    {plural(boxCount, 'box', 'boxes')}.
                  </>
                )}{' '}
                {n > 0 && leftOpen.length === 1 && `${nameOf(leftOpen[0])} stays open for later.`}
                {n > 0 && leftOpen.length > 1 && `${leftOpen.length} lines stay open for later.`}
              </p>
              <Reveal show={!!blockedReason && n > 0}>
                <p className="text-amber-300">{blockedReason}</p>
              </Reveal>
              {n === 0 && <p>{blockedReason}</p>}
              <Reveal show={trackedNames.length > 0}>
                <p data-testid="tracked-reminder">
                  {joinNames(trackedNames)}{' '}
                  {trackedNames.length === 1
                    ? 'is tracked per unit and will need its item QR codes'
                    : 'are tracked per unit and will need their item QR codes'}
                  : print them from the box page after receiving.
                </p>
              </Reveal>
              <Checkbox
                className="pt-0.5"
                label="Also print item stickers"
                checked={printItemStickers}
                onChange={(e) => setPrintItemStickers(e.target.checked)}
              />
            </div>
            <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-shrink-0 sm:flex-row sm:items-center">
              <button type="button" className={TEXT_BUTTON} onClick={goBack}>
                Cancel
              </button>
              <button
                type="button"
                className={FOOTER_BUTTON}
                disabled={!!blockedReason || submitting}
                onClick={handlePress}
              >
                {buttonLabel}
              </button>
            </div>
          </div>
        </footer>
      )}
      {!receivable && <div className="pb-6" />}

      {pendingBody && (
        <ReceiverIdentityModal
          busy={submitting}
          error={identityError}
          onConfirm={({ user_id, pin }) => {
            setIdentityError(null);
            submit({ ...pendingBody, received_as: { user_id, pin } });
          }}
          onClose={() => {
            setPendingBody(null);
            setIdentityError(null);
          }}
        />
      )}

      {over && (
        <OverReceiptDialog
          over={over}
          nameFor={(l) => l.item || (lines.some((x) => x.id === l.expected_receipt_id) ? lineName(l.expected_receipt_id) : null) || l.sku || 'This item'}
          poNumber={over.refusal.po_number || poNumber}
          boxCount={boxCount}
          isAdmin={isAdmin}
          onClose={() => setOver(null)}
          onApprove={() => resubmitFromOver('approve')}
          onDrop={() => resubmitFromOver('drop')}
        />
      )}
    </div>
    </PageLayout>
  );
};

// ── More than the order asked for ────────────────────────────────────────────

const OverReceiptDialog: React.FC<{
  over: OverState;
  /** The line's name: the server's, else this page's own, else the SKU. Never "null". */
  nameFor: (line: OverReceiptLine) => string;
  poNumber: string | null;
  boxCount: number;
  isAdmin: boolean;
  onClose: () => void;
  onApprove: () => void;
  onDrop: () => void;
}> = ({ over, nameFor, poNumber, boxCount, isAdmin, onClose, onApprove, onDrop }) => {
  const overLines = over.refusal.lines;
  const overIds = new Set(overLines.map((l) => l.expected_receipt_id));
  const others = over.body.lines.filter((l) => !overIds.has(l.expected_receipt_id)).length;
  const single = overLines.length === 1 ? overLines[0] : null;
  const singleName = single ? nameFor(single) : '';
  const title = single
    ? 'More arrived than this order asked for'
    : `${NUMBER_WORDS[overLines.length] || overLines.length} lines are over what the order asked for`;

  return (
    <DrawerShell title={title} onClose={onClose}>
      <div className="space-y-4" data-testid="over-receipt-dialog">
        <p className="text-sm text-on-surface-variant" data-testid="over-receipt-identity">
          {single && <span className="font-medium text-on-surface">{singleName}</span>}
          {poNumber ? (
            <>
              {single ? ' on ' : 'On '}
              <span className="font-medium text-on-surface">{poNumber}</span>
            </>
          ) : null}
        </p>

        {single ? (
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-on-surface-variant">This order expects</dt>
              <dd className="tabular-nums text-on-surface">{single.expected}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-on-surface-variant">Already received</dt>
              <dd className="tabular-nums text-on-surface">{single.already_received}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-on-surface-variant">{boxCount > 1 ? 'These boxes' : 'This box'}</dt>
              <dd className="tabular-nums text-on-surface">{single.this_receive}</dd>
            </div>
            <div className="flex justify-between border-t border-outline-variant pt-1.5 font-medium">
              <dt className="text-on-surface-variant">Total if received</dt>
              <dd className="tabular-nums text-amber-300">
                {single.total_after}
                <span className="ml-1.5 text-xs font-normal text-amber-400/80">
                  ({single.total_after - single.expected} extra)
                </span>
              </dd>
            </div>
          </dl>
        ) : (
          <ul className="space-y-2.5 text-sm">
            {overLines.map((l) => (
              <li key={l.expected_receipt_id}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-on-surface">{nameFor(l)}</span>
                  <span className="shrink-0 tabular-nums text-on-surface">
                    {l.total_after} of {l.expected}{' '}
                    <span className="text-amber-300">(+{l.total_after - l.expected})</span>
                  </span>
                </div>
                <p className="text-xs tabular-nums text-on-surface-variant">
                  {l.expected} ordered, {l.already_received} already in, {l.this_receive} in this delivery
                </p>
              </li>
            ))}
          </ul>
        )}

        {isAdmin ? (
          <p className="text-sm text-on-surface-variant">
            {single
              ? `Receiving this puts ${single.total_after} of ${singleName} into stock against an order for ${single.expected}.`
              : 'Receiving these puts more into stock than the order asked for, on every line above.'}{' '}
            There is no undo. Check the boxes and the order first.
          </p>
        ) : (
          <p className="text-sm text-on-surface-variant">
            An admin must receive over-quantities.{' '}
            {others > 0
              ? `You can receive the other ${plural(others, 'line', 'lines')} now and leave ${
                  single ? 'this one' : 'these'
                } where ${single ? 'it is' : 'they are'}; ${
                  single ? 'the line stays' : 'the lines stay'
                } open until an admin receives ${single ? 'it' : 'them'}.`
              : `Leave the boxes where they are and let one know; ${
                  single ? 'the line stays' : 'the lines stay'
                } open until an admin receives ${single ? 'it' : 'them'}.`}
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className={TEXT_BUTTON} onClick={onClose}>
            {isAdmin ? 'Cancel' : 'Close'}
          </button>
          {isAdmin ? (
            <button type="button" className={AMBER_BUTTON} onClick={onApprove}>
              {single ? `Receive ${single.this_receive} anyway` : 'Receive anyway'}
            </button>
          ) : (
            others > 0 && (
              <button type="button" className={TONAL_BUTTON} onClick={onDrop}>
                Receive the other {plural(others, 'line', 'lines')} now
              </button>
            )
          )}
        </div>
      </div>
    </DrawerShell>
  );
};

export default ReceiveOrderPage;
